import React, { useState } from 'react';
import { User } from '../../../types';
import { useAdminTranslation } from '../../../modules/i18n';
import { isActive } from './user-status-utils';

interface MobileUserCardProps {
  user: User;
  onEdit: () => void;
  onDelete: () => void;
  onDisable?: () => void;
  onEnable?: () => void;
  onViewDetail: () => void;
  isCurrentUser?: boolean;
}

const MobileUserCard: React.FC<MobileUserCardProps> = ({ user, onEdit, onDelete, onDisable, onEnable, onViewDetail, isCurrentUser = false }) => {
  const { t } = useAdminTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const toggleMenu = (event: React.MouseEvent) => {
    if (!showMenu) {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        right: window.innerWidth - rect.right
      });
    }
    setShowMenu(!showMenu);
  };

  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-border-dark dark:bg-dark-background-secondary'>
      <div className='flex items-center justify-between space-x-3'>
        <div className='min-w-0 flex-1'>
          <h3 className='truncate text-sm font-medium text-gray-900 dark:text-dark-text'>{user.username}</h3>
          <p className='truncate text-xs text-gray-500 dark:text-dark-text-secondary'>{user.email}</p>
        </div>
        <div className='flex-shrink-0'>
          <span
            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
              user.role?.toUpperCase() === 'ADMIN'
                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                : user.role?.toUpperCase() === 'MODERATOR'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-dark-background-tertiary dark:text-dark-text'
            }`}
          >
            {user.role}
          </span>
        </div>
        <div className='flex-shrink-0'>
          <span
            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
              isActive(user.status)
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}
          >
            {isActive(user.status) ? t('content.users.status.active') : t('content.users.status.inactive')}
          </span>
        </div>
        <div className='relative'>
          <button
            onClick={toggleMenu}
            className='rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-background-hover dark:hover:text-dark-text-secondary'
            title={t('content.users.mobile.optionsButton')}
          >
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z'
              />
            </svg>
          </button>

          {showMenu && (
            <>
              <div className='fixed inset-0 z-40' onClick={() => setShowMenu(false)} />
              <div
                className='fixed z-50 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-border-dark dark:bg-dark-background-secondary'
                style={{
                  top: `${menuPosition.top}px`,
                  right: `${menuPosition.right}px`
                }}
              >
                <div className='py-1'>
                  <button
                    onClick={() => {
                      onViewDetail();
                      setShowMenu(false);
                    }}
                    className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                  >
                    <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                      />
                    </svg>
                    <span>{t('content.users.mobile.actions.viewDetail')}</span>
                  </button>
                  <button
                    onClick={() => {
                      onEdit();
                      setShowMenu(false);
                    }}
                    className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
                  >
                    <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                    <span>{t('content.users.mobile.actions.edit')}</span>
                  </button>
                  {/* Disable/Enable for non-current users */}
                  {!isCurrentUser && isActive(user.status) && onDisable && (
                    <button
                      onClick={() => {
                        onDisable();
                        setShowMenu(false);
                      }}
                      className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-400/30'
                    >
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' />
                      </svg>
                      <span>{t('content.users.actions.disable')}</span>
                    </button>
                  )}
                  {!isCurrentUser && !isActive(user.status) && onEnable && (
                    <button
                      onClick={() => {
                        onEnable();
                        setShowMenu(false);
                      }}
                      className='flex w-full items-center space-x-2 px-4 py-2 text-left text-sm text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-400/30'
                    >
                      <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                      </svg>
                      <span>{t('content.users.actions.enable')}</span>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileUserCard;
